import React, { useState } from 'react';
import ExcelImporter from '../../components/ExcelImporter';
import './ExcelImportExample.css';

const ExcelImportExample = () => {
  const [importedData, setImportedData] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Funzione chiamata quando i dati vengono importati dall'ExcelImporter
  const handleDataImported = (data) => {
    setImportedData(data);
    setProcessedData(null); // Resetta i dati elaborati quando vengono importati nuovi dati
  };

  // Esempio di elaborazione dei dati importati
  const processData = async () => {
    if (!importedData || importedData.length === 0) return;

    setIsProcessing(true);

    // Simulazione di un'elaborazione asincrona
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      // Esempio: calcola la somma di una colonna numerica (se esiste)
      const numericColumns = {};
      const firstRow = importedData[0];
      
      // Identifica le colonne che contengono valori numerici
      Object.keys(firstRow).forEach(key => {
        if (typeof firstRow[key] === 'number') {
          numericColumns[key] = true;
        }
      });

      // Calcola le somme per ogni colonna numerica
      const sums = {};
      Object.keys(numericColumns).forEach(column => {
        sums[column] = importedData.reduce((sum, row) => {
          const value = row[column];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
      });

      // Esempio: raggruppa i dati per una colonna specifica (prima colonna non numerica trovata)
      let groupByColumn = null;
      for (const key in firstRow) {
        if (!numericColumns[key]) {
          groupByColumn = key;
          break;
        }
      }

      let groupedData = {};
      if (groupByColumn) {
        importedData.forEach(row => {
          const groupValue = row[groupByColumn] || 'Non specificato';
          if (!groupedData[groupValue]) {
            groupedData[groupValue] = [];
          }
          groupedData[groupValue].push(row);
        });
      }

      setProcessedData({
        totalRecords: importedData.length,
        sums,
        groupByColumn,
        groupedData
      });
    } catch (error) {
      console.error('Errore durante l\'elaborazione dei dati:', error);
      alert('Si è verificato un errore durante l\'elaborazione dei dati.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="excel-import-example">
      <h2>Esempio di Importazione CSV</h2>
      
      <div className="section">
        <p className="info-text">
          Questa pagina dimostra come importare e processare dati da file CSV. 
          Carica un file CSV con intestazioni e dati numerici per vedere l'elaborazione in azione.
        </p>
        <ExcelImporter onDataImported={handleDataImported} />
      </div>
      
      {importedData && importedData.length > 0 && (
        <div className="section">
          <h3>Dati Importati</h3>
          <p>Sono stati importati {importedData.length} record.</p>
          
          <button 
            className="process-button" 
            onClick={processData}
            disabled={isProcessing}
          >
            {isProcessing ? 'Elaborazione in corso...' : 'Elabora Dati'}
          </button>
        </div>
      )}
      
      {processedData && (
        <div className="section results-section">
          <h3>Risultati dell'Elaborazione</h3>
          
          <div className="result-card">
            <h4>Statistiche Generali</h4>
            <p>Totale record elaborati: {processedData.totalRecords}</p>
          </div>
          
          {Object.keys(processedData.sums).length > 0 && (
            <div className="result-card">
              <h4>Somme per Colonne Numeriche</h4>
              <ul>
                {Object.entries(processedData.sums).map(([column, sum]) => (
                  <li key={column}>
                    <strong>{column}:</strong> {sum.toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {processedData.groupByColumn && (
            <div className="result-card">
              <h4>Dati Raggruppati per "{processedData.groupByColumn}"</h4>
              <div className="grouped-data">
                {Object.entries(processedData.groupedData).map(([group, items]) => (
                  <div key={group} className="group-item">
                    <h5>{group} ({items.length} record)</h5>
                    <ul>
                      {items.slice(0, 3).map((item, index) => (
                        <li key={index}>
                          {Object.entries(item)
                            .filter(([key]) => key !== processedData.groupByColumn)
                            .map(([key, value]) => (
                              <span key={key}>
                                <strong>{key}:</strong> {value}{' '}
                              </span>
                            ))}
                        </li>
                      ))}
                      {items.length > 3 && <li>... e altri {items.length - 3} record</li>}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="note">
            <p>
              <strong>Nota:</strong> Questa è solo una dimostrazione di come i dati importati possono essere elaborati.
              In un'applicazione reale, l'elaborazione dipenderà dalle specifiche esigenze del progetto.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelImportExample;